import { useState, useMemo, useEffect } from 'react';
import { TextInput } from '@mantine/core';
import debounce from 'lodash/debounce';
import { ExtendedColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { FILTER_DEBOUNCE_MS } from '@dealstack/frontend/shared/components/Table/MantineTable/constants';

const filterValueToText = (value: unknown): string => {
	if (Array.isArray(value)) {
		return value.join(', ');
	}
	if (typeof value === 'string') {
		return value;
	}
	return '';
};

interface Props<TData, TValue> {
	column: ExtendedColumn<TData, TValue>;
	filterText?: string;
	maxLength?: number;
}

export const TextInputFilter = <TData, TValue>({
	column,
	filterText,
	maxLength,
}: Props<TData, TValue>) => {
	const { getFilterValue, setFilterValue } = column;

	const columnFilterValue = filterValueToText(getFilterValue());
	const [localValue, setLocalValue] = useState(columnFilterValue);

	const debouncedSetFilter = useMemo(
		() => debounce(setFilterValue, FILTER_DEBOUNCE_MS),
		[setFilterValue]
	);

	useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);

	useEffect(() => {
		setLocalValue(columnFilterValue);
	}, [columnFilterValue]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.currentTarget.value;
		setLocalValue(value);
		debouncedSetFilter(value);
	};

	return (
		<TextInput
			value={localValue}
			onChange={handleChange}
			placeholder={filterText}
			maxLength={maxLength}
			size="sm"
			styles={{
				input: {
					borderColor: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
					'&:hover': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
					'&:focus': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
					'&:focus-within': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
				},
			}}
		/>
	);
};
